export type Plan = 'weekly' | 'monthly' | 'lifetime'
export type ProductStatus = 'draft' | 'active' | 'archived'
export type OrderStatus =
  | 'pending'
  | 'active'
  | 'paid'
  | 'past_due'
  | 'paused'
  | 'canceled'
  | 'expired'
  | 'failed'

export interface WeaoStatus {
  title: string
  version: string | null
  working: boolean
  detected: boolean
  updatedDate: string | null
  extype: string | null
}

export interface Product {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  plans: Plan[]
  pricing: {
    weekly: number
    monthly: number
    lifetime: number
    currency: string
  }
  stock: number
  stockByPlan: Record<Plan, number>
  status: ProductStatus
  accent: string
  features: string[]
  rating: number
  reviewsCount: number
  tag: string | null
  weaoSlug: string | null
  weao?: WeaoStatus | null
  createdAt: string
  updatedAt: string
}

export interface Review {
  id: string
  rating: number
  body: string
  author: {
    name: string
    avatarUrl: string | null
  }
  verifiedPurchase: boolean
  createdAt: string
  updatedAt: string
}

export interface Order {
  id: string
  product: {
    id: string
    name: string
    slug: string
  }
  plan: Plan
  amount: number
  currency: string
  status: OrderStatus
  deliveryStatus: 'pending' | 'delivered'
  accessKey: string | null
  createdAt: string
  updatedAt: string
}

export interface AccountProfile {
  userId: string
  email: string | null
  displayName: string
  avatarUrl: string | null
  isAdmin: boolean
}

export interface AdminOrder {
  id: string
  productName: string
  plan: Plan
  amount: number
  currency: string
  status: OrderStatus
  deliveryStatus: 'pending' | 'delivered'
  createdAt: string
}

export interface AdminDelivery {
  id: string
  productName: string
  plan: Plan
  amount: number
  currency: string
  createdAt: string
}

export type KeyDuration = 'weekly' | 'monthly'

export interface AdminKey {
  id: string
  value: string
  status: 'available' | 'delivered' | 'revoked'
  duration: KeyDuration | 'any'
  reserved: boolean
  orderId: string | null
  createdAt: string
  deliveredAt: string | null
}

export interface KeyStats {
  ready: Record<Plan, number>
  delivered: number
}

export interface AdminOrderDetail {
  id: string
  productName: string
  plan: Plan
  amount: number
  currency: string
  status: OrderStatus
  deliveryStatus: 'pending' | 'delivered'
  keyValue: string | null
  email: string | null
  createdAt: string
}

export const LICENSE_CATEGORIES: { plan: Plan; label: string; short: string }[] = [
  { plan: 'weekly', label: '1 week', short: '1w' },
  { plan: 'monthly', label: '1 month', short: '1mo' },
  { plan: 'lifetime', label: 'Lifetime', short: 'life' },
]

export interface AdminOverview {
  products: Product[]
  keyStats: Record<string, KeyStats>
  pendingDeliveries: AdminDelivery[]
  stats: {
    orders: number
    subscriptions: number
    bookedCents: number
    reviews: number
    publishedReviews: number
    hiddenReviews: number
  }
  recentOrders: AdminOrder[]
}

export interface AdminReview {
  id: string
  productName: string
  rating: number
  body: string
  displayName: string
  verifiedPurchase: boolean
  status: 'published' | 'hidden'
  createdAt: string
}
